import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import {
  updateTaskLabelGroupLabelOrder,
  updateTaskLabelGroupOrder,
  updateTaskLabelingColumnNumber,
  updateTaskLabelSuggest,
} from '../../../states/containers/common/currentSlice'
import { compile } from '../../../utils/search'
import Tips from '../common/Tips'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import { dateToTodayElapsedSeconds, nowDate, secToHour, secToMinute, secToTwoDigitsHour, secToTwoDigitsMinute } from '../../../utils/date'

import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import SortableItem from './SortableItem'
import CustomStrategy from './CustomStrategy'
import { fetchTaskRecentlyUsedLabelGroup, fetchTaskSearchLabelGroup } from '../../../states/containers/task/taskSlice'
import LoadingSquare from '../common/LoadingSquare'

interface TaskDetail {
  task_id: number
  type: string
  origin_task_id: number | null
  time: number
  service_name: string | null
  icon: string | null
  icon_color: string | null
  task_name: string | null
  file_path: string | null
  included_url_parts: string | null
  exclusion_id: number | null
  timeline: {
    start_time: string
    end_time: string
  }[]
  label: {
    label_group_id: number
    label_group_name: string
    label_group_color: string
    label_id: number
    label_name: string
    label_color: string
    disabled: boolean
    status: string
  } | null
  is_loading?: boolean
}

interface LabelList {
  label_id: number
  label_name: string
  label_color: string
  label_code?: string | null
  description: string | null
  disabled?: boolean
}

interface LabelGroupList {
  label_group_id: number
  label_group_name: string
  label_group_color?: string
  label_group_code?: string | null
  disabled?: boolean
  labels: LabelList[]
}

interface SuggestLabelGroups {
  label_group_id: number
  label_group_name: string
  label_group_code: string | null
  label_group_color: string
  disabled: boolean
  labels: {
    label_id: number
    label_name: string
    label_code: string | null
    label_color: string
    description: string | null
    disabled: boolean
  }[]
}

interface LabelCommon {
  id: number
  name: string
  code: string | null
  color: string
}

interface Props {
  taskStatus: {ids: number[], x: number | "auto", y: number | "auto", isFixed?: boolean}
  labelGroupOriginList: LabelGroupList[]
  callbackLabels: (taskIds: number[], labelGroup: LabelCommon, labels: LabelCommon[]) => void
  closeCallback: () => void
  isClear?: boolean
  currentMemberId: number
  taskDetails: TaskDetail[]
  title?: string
  date?: string
}

const TIME_LIST = Array(25).fill(null).map((_v, i) => `${i + 5}:00`)

const AddLabelBigModal: FC<Props> = (props) => {
  const {
    taskStatus,
    labelGroupOriginList,
    callbackLabels,
    closeCallback,
    isClear,
    currentMemberId,
    taskDetails,
    title,
    date,
  } = props

  const {
    current,
    login,
  } = useSelector((state: RootState) => state)

  const elementRef = useRef<HTMLDivElement>(null)

  const dispatch = useDispatch()

  const [selectLabelIds, setSelectLabelIds] = useState<number[]>([])
  const [labelGroupListInit, setLabelGroupListInit] = useState<LabelGroupList[]>([])
  const [labelGroupList, setLabelGroupList] = useState<LabelGroupList[]>([])
  const [searchLabelWord, setSearchLabelWord] = useState<string>("")

  const [labelDescription, setLabelDescription] = useState<string | null>("")

  const [descriptionPosition, setDescriptionPosition] = useState<{x: number, y: number, reverse: boolean}>({ x: 0, y: 0, reverse: false })

  const [displayDescription, setDisplayDescription] = useState<boolean>(false)

  const [openLabelGroupIds, setOpenLabelGroupIds] = useState<number[]>([])

  const [suggestLabelList, setSuggestLabelList] = useState<SuggestLabelGroups[]>([])

  const [isOpenSuggest, setIsOpenSuggest] = useState<boolean>(true)
  const [labelGroupOrder, setLabelGroupOrder] = useState<number[]>([])

  const [labelGroupLabelOrder, setLabelGroupLabelOrder] = useState<{ id: number, label_ids: number[] }[]>([])

  const [isWordLoading, setIsWordLoading] = useState<boolean>(false)
  const [composition, setComposition] = useState<boolean>(false)
  const [isSearched, setIsSearched] = useState<boolean>(false)

  const [columnNumber, setColumnNumber] = useState<number>(4)

  const [width, setWidth] = useState(window.innerWidth)

  const targetTaskAreaHeight = useMemo(() => {
    if (!taskDetails.length) return 40
    const tmpListHeight = (taskDetails.length * 14) + ((taskDetails.length - 1) * 8) + 60 + 40
    return tmpListHeight > 200 ? 200 : tmpListHeight
  }, [taskDetails])

  const blockWidth = useMemo(() => {
    const modalWidth = elementRef.current?.getBoundingClientRect().width || 0
    return (modalWidth - 40 - ((columnNumber - 1) * (8 + 2))) / columnNumber
  }, [elementRef.current, width, columnNumber])

  const resetApp = () => {
    setSearchLabelWord("")
    setLabelGroupList(labelGroupListInit)
    setOpenLabelGroupIds([])
  }

  const searchApp = (word?: string) => {
    let words: string[] = []
    if (word !== undefined) {
      words = word.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase())
    } else {
      words = searchLabelWord.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase())
    }
    if (!words.length) {
      resetApp()
      return
    }
    if (login.permissions?.high_volume) {
      setIsWordLoading(true)
      dispatch(
        fetchTaskSearchLabelGroup(
          {
            member_id: currentMemberId,
            keywords: words,
          },
          (result) => {
            setLabelGroupList(result.label_groups)
            setIsWordLoading(false)
            setIsSearched(true)
          },
        ),
      )
      return
    }
    setOpenLabelGroupIds(labelGroupListInit.map((v) => v.label_group_id))
    const tmp = labelGroupListInit.map((labelGroup) => {
      if (compile(words).test((labelGroup.label_group_name + labelGroup.label_group_code).toLowerCase())) {
        return { ...labelGroup }
      }
      return {
        ...labelGroup,
        labels: labelGroup.labels.filter((label) => !label.disabled && compile(words)
          .test((label.label_name + label.label_code).toLowerCase())),
      }
    })
    const list = tmp.filter((value) => value.labels.length)
    setLabelGroupList(list)
    setIsSearched(true)
  }

  const inputSearchWord = (word: string) => {
    setSearchLabelWord(word)
    if (!login.permissions?.high_volume) searchApp(word)
  }

  const clickLabel = (labelGroup: LabelGroupList, label: LabelList) => {
    const html = document.querySelector('html')
    if (!html) return
    html.classList.remove('modal_open')
    callbackLabels(
      taskStatus.ids,
      {
        id: labelGroup.label_group_id,
        name: labelGroup.label_group_name,
        color: labelGroup.label_group_color || "#666666",
        code: labelGroup.label_group_code || null,
      },
      [{
        id: label.label_id,
        name: label.label_name,
        color: label.label_color || "#666666",
        code: label.label_code || null,
      }],
    )
    const tmpLabelSuggest = (current.taskLabelSuggest.length)
      ? (
        current.taskLabelSuggest.map((v) => {
          if (v.member_id === currentMemberId) {
            return ({
              member_id: v.member_id,
              labels: [{
                label_group_id: labelGroup.label_group_id,
                label_group_name: labelGroup.label_group_name || "",
                label_group_code: labelGroup.label_group_code || null,
                label_id: label.label_id,
                label_name: label.label_name || "",
                label_color: label.label_color || "",
                label_code: label.label_code || null,
              }, ...v.labels.filter((w) => w.label_group_id !== labelGroup.label_group_id || w.label_id !== label.label_id)].slice(0, 10),
            })
          }
          return v
        })
      ) : (
        []
      )
    if (!current.taskLabelSuggest.length || !current.taskLabelSuggest.find((v) => v.member_id === currentMemberId)?.member_id) {
      tmpLabelSuggest.push(
        {
          member_id: currentMemberId,
          labels: [{
            label_group_id: labelGroup.label_group_id,
            label_group_name: labelGroup.label_group_name || "",
            label_group_code: labelGroup.label_group_code || null,
            label_id: label.label_id,
            label_name: label.label_name || "",
            label_color: label.label_color || "",
            label_code: label.label_code || null,
          }],
        },
      )
    }
    dispatch(updateTaskLabelSuggest(tmpLabelSuggest))
    resetApp()
    closeCallback()
  }

  const clickNotBelongLabel = () => {
    callbackLabels(
      taskStatus.ids,
      { id: 0, name: "プロジェクトに属さないタスク", code: null, color: "#339BB2" },
      [{ id: 0, name: "プロジェクトに属さないタスク", code: null, color: "#339BB2" }],
    )
    closeCallback()
    const html = document.querySelector('html')
    if (!html) return
    html.classList.remove('modal_open')
  }

  const clickBreakLabel = () => {
    callbackLabels(
      taskStatus.ids,
      { id: 1, name: "休憩", code: null, color: "#FFA26B" },
      [{ id: 1, name: "休憩", code: null, color: "#FFA26B" }],
    )
    closeCallback()
    const html = document.querySelector('html')
    if (!html) return
    html.classList.remove('modal_open')
  }

  const hideLabelDescription = () => {
    setDisplayDescription(false)
    setLabelDescription("")
  }

  const displayLabelDescription = (text: string | null, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (!text) {
      hideLabelDescription()
      return
    }
    setDisplayDescription(true)
    setLabelDescription(text)
    let xPosition = event.clientX - (elementRef.current?.getBoundingClientRect().x || 0)
    let reverse = false
    if ((elementRef.current?.getBoundingClientRect().width || 0) / 2 < event.clientX) {
      xPosition = (elementRef.current?.getBoundingClientRect().width || 0) - xPosition
      reverse = true
    }
    setDescriptionPosition({
      x: xPosition - 80,
      y: event.clientY - (elementRef.current?.getBoundingClientRect().y || 0) + 16,
      reverse: reverse,
    })
  }

  const close = () => {
    closeCallback()
    const html = document.querySelector('html')
    if (!html) return
    html.classList.remove('modal_open')
  }

  const changeOpenLabelGroup = (labelGroupId: number) => {
    if (openLabelGroupIds.includes(labelGroupId)) {
      setOpenLabelGroupIds(openLabelGroupIds.filter((v) => v !== labelGroupId))
    } else {
      setOpenLabelGroupIds([...openLabelGroupIds, labelGroupId])
    }
  }

  const buildTime = (dateTime: string, beforeDateTime: string) => {
    const tmpSec = dateToTodayElapsedSeconds(dateTime, beforeDateTime)
    return `${secToTwoDigitsHour(tmpSec)}時${secToTwoDigitsMinute(tmpSec)}分`
  }

  const rulerBlock = () => (
    <div
      className="timeline_ruler_area"
      style={{
        position: "absolute",
        height: "24px",
      }}
    >
      {
        TIME_LIST.map((v) => (
          <div key={`ruler_${v}`} />
        ))
      }
      <div />
    </div>
  )

  const labelBlock = () => (
    <div
      className="timeline_label_area"
      style={{
        width: `${(5 / 6) * 100}%`,
        marginLeft: `${(1 / 6) * 100}%`,
        position: "absolute",
        top: 0,
        height: "16px",
        marginTop: "4px",
        fontSize: "10px",
        color: "#666666",
      }}
    >
      {
        TIME_LIST.map((v, i) => (
          <React.Fragment key={v}>
            <div
              style={{
                position: "absolute",
                left: `${(i / 24) * 100}%`,
                transform: "translateX(-50%)",
                padding: "2px 4px",
              }}
            >
              {v}
            </div>
          </React.Fragment>
        ))
      }
    </div>
  )

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

  const handleDragStart = useCallback(() => {
    setOpenLabelGroupIds([])
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (active.id !== over?.id) {
      setLabelGroupOrder((v) => {
        const oldIndex = v.indexOf(Number(active.id))
        const newIndex = v.indexOf(Number(over?.id))
        const tmpLabelGroupIds = arrayMove(v, oldIndex, newIndex)
        const tmpList = current.taskLabelGroupOrder.map((w) => (w.member_id === currentMemberId ? ({ ...w, label_group_ids: tmpLabelGroupIds }) : w))
        if (!tmpList.length || !tmpList.find((w) => w.member_id === currentMemberId)) {
          tmpList.push({ member_id: currentMemberId, label_group_ids: tmpLabelGroupIds })
        }
        dispatch(updateTaskLabelGroupOrder(tmpList))
        return tmpLabelGroupIds
      })
    }
  }, [])

  const handleDragEndForLabel = useCallback((event: DragEndEvent, labelGroupId: number) => {
    const { active, over } = event
    if (active.id !== over?.id) {
      setLabelGroupLabelOrder((labelGroups) => {
        const tmpList = labelGroups.map((labelGroup) => {
          if (labelGroup.id === labelGroupId) {
            const oldIndex = labelGroup.label_ids.indexOf(Number(active.id))
            const newIndex = labelGroup.label_ids.indexOf(Number(over?.id))
            const tmpLabelGroupLabels = arrayMove(labelGroup.label_ids, oldIndex, newIndex)
            return ({ id: labelGroup.id, label_ids: tmpLabelGroupLabels })
          }
          return labelGroup
        })
        dispatch(
          updateTaskLabelGroupLabelOrder(
            current.taskLabelGroupLabelOrder.map((v) => (v.member_id === currentMemberId ? ({ ...v, label_groups: tmpList }) : v)),
          ),
        )
        return tmpList
      })
    }
  }, [])

  const resetSort = () => {
    setSearchLabelWord("")
    setOpenLabelGroupIds([])
    const tmpLabelGroupIds = labelGroupOriginList.filter((v) => !v.disabled).map((v) => v.label_group_id)
    setLabelGroupOrder(tmpLabelGroupIds)
    const tmpList = current.taskLabelGroupOrder.map((w) => (w.member_id === currentMemberId ? ({ ...w, label_group_ids: tmpLabelGroupIds }) : w))
    if (!tmpList.length || !tmpList.find((v) => v.member_id === currentMemberId)) {
      tmpList.push({ member_id: currentMemberId, label_group_ids: tmpLabelGroupIds })
    }
    dispatch(updateTaskLabelGroupOrder(tmpList))
    // eslint-disable-next-line max-len
    const tmpLabelGroupLabels = labelGroupOriginList.filter((v) => !v.disabled).map((v) => ({ id: v.label_group_id, label_ids: v.labels.map((w) => w.label_id) }))
    setLabelGroupLabelOrder(tmpLabelGroupLabels)
    // eslint-disable-next-line max-len
    const tmpLabelList = current.taskLabelGroupLabelOrder.map((w) => (w.member_id === currentMemberId ? ({ ...w, label_groups: tmpLabelGroupLabels }) : w))
    if (!tmpLabelList.length || !tmpLabelList.find((v) => v.member_id === currentMemberId)) {
      tmpLabelList.push({ member_id: currentMemberId, label_groups: tmpLabelGroupLabels })
    }
    dispatch(updateTaskLabelGroupLabelOrder(tmpLabelList))
  }

  const changeColumnNumber = (num: number) => {
    setColumnNumber(num)
    dispatch(updateTaskLabelingColumnNumber(num))
  }

  useEffect(() => {
    setColumnNumber(current.taskLabelingColumnNumber || 4)
  }, [])

  useEffect(() => {
    if (login.permissions?.high_volume) return
    setLabelGroupList(labelGroupOriginList.filter((v) => !v.disabled) || [])
    setLabelGroupListInit(labelGroupOriginList.filter((v) => !v.disabled) || [])
  }, [labelGroupOriginList])

  useEffect(() => {
    if (!isClear) return
    setSelectLabelIds([])
  }, [isClear])

  useEffect(() => {
    const html = document.querySelector('html')
    if (!html) return
    if (labelGroupOriginList) {
      const scroll = window.scrollY
      html.classList.add('modal_open')
      html.style.top = `-${scroll}px`
    }
  }, [labelGroupOriginList])

  useEffect(() => {
    if (!labelGroupOriginList.length) return
    let tmpLabelGroupIds: number[] = []
    let tmpLabelGroupLabels: { id: number, label_ids: number[] }[] = []
    const targetList = current.taskLabelGroupOrder.find((v) => v.member_id === currentMemberId)
    if (!targetList) {
      tmpLabelGroupIds = labelGroupOriginList.map((v) => v.label_group_id)
    } else {
      const originIds = labelGroupOriginList.map((v) => v.label_group_id)
      tmpLabelGroupIds = [
        ...originIds.filter((v) => !targetList.label_group_ids.includes(v)),
        ...targetList.label_group_ids.filter((v) => originIds.includes(v)),
      ]
    }
    setLabelGroupOrder(tmpLabelGroupIds)
    const targetLabelList = current.taskLabelGroupLabelOrder.find((v) => v.member_id === currentMemberId)?.label_groups
    if (!targetLabelList) {
      tmpLabelGroupLabels = labelGroupOriginList.map((v) => ({ id: v.label_group_id, label_ids: v.labels.map((w) => w.label_id) }))
    } else {
      const originLabels = labelGroupOriginList.map((v) => ({ id: v.label_group_id, label_ids: v.labels.map((w) => w.label_id) }))
      tmpLabelGroupLabels = [
        ...originLabels.filter((v) => !targetLabelList.map((w) => w.id).includes(v.id)),
        ...targetLabelList.filter((v) => originLabels.map((w) => w.id).includes(v.id)).map((v) => ({
          ...v,
          label_ids: Array.from(new Set([...v.label_ids, ...originLabels.find((w) => w.id === v.id)?.label_ids || []])),
        })),
      ]
    }
    setLabelGroupLabelOrder(tmpLabelGroupLabels)
    const tmpList = current.taskLabelGroupOrder.map((w) => (w.member_id === currentMemberId ? ({ ...w, label_group_ids: tmpLabelGroupIds }) : w))
    if (!tmpList.length || !tmpList.find((v) => v.member_id === currentMemberId)) {
      tmpList.push({ member_id: currentMemberId, label_group_ids: tmpLabelGroupIds })
    }
    dispatch(updateTaskLabelGroupOrder(tmpList))
    // eslint-disable-next-line max-len
    const tmpLabelList = current.taskLabelGroupLabelOrder.map((w) => (w.member_id === currentMemberId ? ({ ...w, label_groups: tmpLabelGroupLabels }) : w))
    if (!tmpLabelList.length || !tmpLabelList.find((v) => v.member_id === currentMemberId)) {
      tmpLabelList.push({ member_id: currentMemberId, label_groups: tmpLabelGroupLabels })
    }
    dispatch(updateTaskLabelGroupLabelOrder(tmpLabelList))
  }, [labelGroupOriginList])

  useEffect(() => {
    if (!labelGroupOrder.length || !labelGroupLabelOrder.length) return
    const tmpLabelGroupList = labelGroupOrder.map((v) => labelGroupList.find((w) => w.label_group_id === v)).filter(Boolean) as LabelGroupList[]
    setLabelGroupList(tmpLabelGroupList.map((v) => ({
      ...v,
      // eslint-disable-next-line max-len
      labels: labelGroupLabelOrder.find((w) => w.id === v.label_group_id)?.label_ids.map((w) => v.labels.find((x) => x.label_id === w) || false).filter(Boolean) as LabelList[] || [],
    })))
    // eslint-disable-next-line max-len
    const tmpLabelGroupListInit = labelGroupOrder.map((v) => labelGroupOriginList.find((w) => w.label_group_id === v)).filter(Boolean) as LabelGroupList[]
    setLabelGroupListInit(tmpLabelGroupListInit.map((v) => ({
      ...v,
      // eslint-disable-next-line max-len
      labels: labelGroupLabelOrder.find((w) => w.id === v.label_group_id)?.label_ids.map((w) => v.labels.find((x) => x.label_id === w) || false).filter(Boolean) as LabelList[] || [],
    })))
  }, [labelGroupOrder, labelGroupLabelOrder])

  useEffect(() => {
    if (!current.taskLabelSuggest) return
    dispatch(
      fetchTaskRecentlyUsedLabelGroup(
        {
          member_id: currentMemberId,
          label_ids: current.taskLabelSuggest.find((v) => v.member_id === currentMemberId)?.labels.map((v) => v.label_id) || [],
        },
        (result) => {
          const tmpTaskLabelSuggest = current.taskLabelSuggest.find((v) => v.member_id === currentMemberId)
          const tmpLabelGroupOrder = Array.from(new Set(tmpTaskLabelSuggest?.labels.map((v) => v.label_group_id)))
          const tmpLabelOrder = Array.from(new Set(tmpTaskLabelSuggest?.labels.map((v) => v.label_id)))
          const tmpList = tmpLabelGroupOrder.map((v) => result.label_groups.find((w) => w.label_group_id === v)).filter(Boolean).map((v) => ({
            ...v,
            labels: tmpLabelOrder.map((w) => v?.labels.find((x) => x.label_id === w)).filter(Boolean),
          })).filter(Boolean) as SuggestLabelGroups[]
          setSuggestLabelList(tmpList as SuggestLabelGroups[])
        },
      ),
    )
  }, [current.taskLabelSuggest])

  useEffect(() => {
    if (!labelGroupListInit.length && !login.permissions?.high_volume) {
      return undefined
    }
    setIsSearched(false)
    const timer = setTimeout(() => {
      if (!composition) {
        searchApp()
      }
    }, 700)
    return () => clearTimeout(timer)
  }, [searchLabelWord, composition])

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <>
      <div
        className="search_box label_select"
        ref={elementRef}
        style={{
          position: "fixed",
          inset: "40px",
          border: "solid 1px #CCCCCC",
          color: "#666666",
          zIndex: "1002110",
        }}
      >
        <div
          style={{
            fontSize: "20px",
            fontWeight: "bold",
            display: "flex",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          {title}
        </div>
        <FontAwesomeIcon
          icon={['fas', 'times']}
          onClick={() => close()}
          style={{
            position: "absolute",
            top: "8px",
            right: "12px",
            cursor: "pointer",
            fontSize: "20px",
          }}
        />
        {
          !!taskDetails.length && (
            <div
              style={{
                display: "flex",
                flexFlow: "column",
                gap: "8px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  position: "relative",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                  }}
                >
                  対象の作業データ
                </div>
                {labelBlock()}
              </div>
              <div
                style={{
                  maxHeight: "120px",
                  overflowY: "scroll",
                  border: "solid 1px #CCCCCC",
                  borderRadius: "4px",
                  backgroundColor: "#FAFAFA",
                  scrollbarWidth: "none",
                }}
              >
                <div
                  className="timeline"
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    marginBottom: 0,
                    border: "none",
                    backgroundColor: "transparent",
                  }}
                >
                  {
                    taskDetails.map((v, i) => (
                      <div
                        className="timeline_row"
                        key={uniqueId()}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          position: "relative",
                          height: "34px",
                          borderBottom: i + 1 === taskDetails.length ? "none" : "1px solid #CCCCCC",
                        }}
                      >
                        {rulerBlock()}
                        {
                          date && v.timeline.map((w) => ({
                            ...w, start_sec: dateToTodayElapsedSeconds(w.start_time, date), end_sec: dateToTodayElapsedSeconds(w.end_time, date),
                          })).map((value) => (
                            <div
                              key={`timeline_${value.start_time}`}
                              className={
                                `timeline_bar${v.type !== "ActiveTask" ? " inactive" : ""}`
                              }
                              style={{
                                left: `${((((value.start_sec - 18000) / 86400) * (5 / 6)) + (1 / 6)) * 100}%`,
                                width:
                                `${(((value.end_sec - value.start_sec) / 86400) * (5 / 6)) * 100}%`,
                                position: "absolute",
                                top: "3px",
                                height: "27px",
                              }}
                            >
                              <Tips
                                description={`${buildTime(value.start_time, date)} ~ ${buildTime(value.end_time, date)}`}
                                className=""
                                icon={false}
                              >
                                <></>
                              </Tips>
                            </div>
                          ))
                        }
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            marginLeft: "8px",
                            fontSize: "14px",
                          }}
                          title={v.task_name || ""}
                        >
                          <div
                            className="icon_area"
                            style={{
                              color: v.icon_color || (v.type !== "ActiveTask" && v.task_name ? "#008800" : "#FF0000"),
                              fontSize: "18px",
                            }}
                          >
                            {
                              v.icon ? (
                                <FontAwesomeIcon
                                  icon={[v.icon.split(" ")[0] as IconPrefix, v.icon.split(" ")[1] as IconName]}
                                  fixedWidth
                                />
                              ) : (
                                <>
                                  {
                                    v.type === "OnlineMeetingTask" ? (
                                      <FontAwesomeIcon
                                        icon={["fas", "video"]}
                                        fixedWidth
                                      />
                                    ) : (
                                      <FontAwesomeIcon
                                        icon={v.task_name ? ["fas", "check-circle"] : ["fas", "exclamation-triangle"]}
                                        fixedWidth
                                      />
                                    )
                                  }
                                </>
                              )
                            }
                          </div>
                          <div>
                            {v.task_name}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "2px",
                            marginRight: "8px",
                          }}
                        >
                          <div>
                            <FontAwesomeIcon icon={['far', 'clock']} />
                          </div>
                          <div>
                            {
                              v.time >= 60 ? (
                                <>
                                  {
                                    secToHour(v.time) ? (
                                      <>
                                        {`${secToHour(v.time)}時間`}
                                      </>
                                    ) : (
                                      <></>
                                    )
                                  }
                                  {
                                    secToMinute(v.time) ? (
                                      <>
                                        {`${secToMinute(v.time)}分`}
                                      </>
                                    ) : (
                                      <></>
                                    )
                                  }
                                </>
                              ) : (
                                <>
                                  1分未満
                                </>
                              )
                            }
                          </div>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
          )
        }
        <div
          className="search_detail"
          style={{
            width: "auto",
            maxHeight: `calc(100% - ${targetTaskAreaHeight}px)`,
            overflow: "scroll",
            overflowY: "scroll",
          }}
        >
          {
            displayDescription && (
              <div
                style={{
                  position: "absolute",
                  top: `${descriptionPosition.y}px`,
                  left: descriptionPosition.reverse ? "unset" : `${descriptionPosition.x}px`,
                  right: descriptionPosition.reverse ? `${descriptionPosition.x}px` : "unset",
                  maxWidth: "500px",
                  backgroundColor: "#edfbfb",
                  padding: "8px 16px",
                  border: "solid 1px #4a91dd",
                  borderRadius: "4px",
                  zIndex: "2000",
                  display: "flex",
                  flexFlow: "column",
                  gap: "4px",
                }}
                onMouseLeave={() => hideLabelDescription()}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    position: "relative",
                  }}
                >
                  <FontAwesomeIcon icon={['far', 'comment-dots']} />
                  <FontAwesomeIcon
                    icon={['fas', 'times']}
                    onClick={() => hideLabelDescription()}
                    style={{
                      position: "absolute",
                      right: 0,
                      cursor: "pointer",
                    }}
                  />
                </div>
                <div>
                  {
                    labelDescription && labelDescription.split('\n').map((v) => (
                      <div
                        key={uniqueId()}
                        style={{
                          minHeight: "1em",
                        }}
                      >
                        {v}
                      </div>
                    ))
                  }
                </div>
              </div>
            )
          }
          <div className="category">
            <div className="category_title">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "24px",
                }}
              >
                業務を選択
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {
                    Array(3).fill(null).map((_v, i) => (
                      <button
                        key={`column_number_${i + 2}`}
                        type="button"
                        onClick={() => changeColumnNumber(i + 2)}
                        style={{
                          borderRadius: "4px",
                          backgroundColor: columnNumber === i + 2 ? "#3e63fe" : "#8da2ff",
                          fontSize: "10px",
                          color: "#FFFFFF",
                          padding: "4px 8px",
                        }}
                      >
                        {`${i + 2}列表示`}
                      </button>
                    ))
                  }
                </div>
              </div>
              {
                selectLabelIds.length ? (
                  <div
                    className="select_reset"
                    role="none"
                    onClick={() => setSelectLabelIds([])}
                  >
                    リセット
                  </div>
                ) : (
                  <></>
                )
              }
              <div className="special_button_area">
                <button
                  type="button"
                  className="break"
                  onClick={clickBreakLabel}
                >
                  休憩
                </button>
                <button
                  type="button"
                  className="other"
                  onClick={clickNotBelongLabel}
                >
                  プロジェクトに属さない作業データ
                </button>
              </div>
            </div>
            {
              !!suggestLabelList.length && (
                <>
                  <div
                    className="group_title"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "8px 4px",
                      cursor: "pointer",
                      backgroundColor: isOpenSuggest ? "transparent" : "#fff8f2",
                      userSelect: "none",
                    }}
                    role="none"
                    onClick={() => setIsOpenSuggest(!isOpenSuggest)}
                  >
                    <div>
                      最近使ったプロジェクト・業務
                    </div>
                    <div>
                      <div
                        style={{
                          display: "flex",
                          gap: "2px",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "8px",
                          }}
                        >
                          {isOpenSuggest ? "閉じる" : "開く"}
                        </div>
                        <FontAwesomeIcon
                          icon={['fas', isOpenSuggest ? 'angle-down' : 'angle-right']}
                        />
                      </div>
                    </div>
                  </div>
                  {
                    isOpenSuggest && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "flex-start",
                          gap: "8px",
                          padding: "8px",
                          backgroundColor: "#fff8f2",
                          marginBottom: "8px",
                        }}
                      >
                        {
                          suggestLabelList.map((labelGroup) => (
                            <div
                              key={`suggest_label_group_${labelGroup.label_group_id}`}
                              style={{
                                width: `calc((100% - ${(columnNumber - 1) * 8}px) / ${columnNumber})`,
                                border: "solid 1px #CCCCCC",
                                backgroundColor: "#FFFFFF",
                              }}
                            >
                              <div
                                className="group_title"
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "12px",
                                  userSelect: "none",
                                  margin: 0,
                                  backgroundColor: "#FAFAFA",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  {
                                    labelGroup.label_group_color ? (
                                      <>
                                        <div
                                          style={{
                                            backgroundColor: labelGroup.label_group_color,
                                            width: "12px",
                                            height: "12px",
                                          }}
                                        />
                                      </>
                                    ) : (
                                      <></>
                                    )
                                  }
                                  <div
                                    style={{
                                      textOverflow: "ellipsis",
                                      overflow: "hidden",
                                      maxWidth: `${blockWidth - 50}px`,
                                    }}
                                  >
                                    {labelGroup.label_group_name}
                                    {!!labelGroup.label_group_code && ` (${labelGroup.label_group_code})`}
                                  </div>
                                </div>
                              </div>
                              <div className="chip_list" style={{ border: "none" }}>
                                {
                                  labelGroup.labels.map((label) => (
                                    <button
                                      type="button"
                                      className={`chip ${selectLabelIds.includes(label.label_id) ? "checked" : ""}`}
                                      key={`suggest_label_${labelGroup.label_group_id}_${label.label_id}`}
                                      onClick={() => clickLabel(
                                        {
                                          label_group_id: labelGroup.label_group_id || 0,
                                          label_group_name: labelGroup.label_group_name || "",
                                          labels: [],
                                        },
                                        {
                                          label_id: label.label_id,
                                          label_name: label.label_name,
                                          label_color: label.label_color || "",
                                          // eslint-disable-next-line max-len
                                          description: labelGroupList.find((v) => v.label_group_id === labelGroup.label_group_id)?.labels.find((v) => v.label_id === label.label_id)?.description || null,
                                        },
                                      )}
                                      // eslint-disable-next-line max-len
                                      onMouseMove={(e) => displayLabelDescription(labelGroupList.find((v) => v.label_group_id === labelGroup.label_group_id)?.labels.find((v) => v.label_id === label.label_id)?.description || null, e)}
                                      onMouseLeave={() => hideLabelDescription()}
                                      onFocus={() => { /* none */ }}
                                    >
                                      {
                                        label.label_color ? (
                                          <>
                                            <div
                                              style={{
                                                backgroundColor: label.label_color,
                                                borderRadius: "50%",
                                                width: "12px",
                                                height: "12px",
                                              }}
                                            />
                                          </>
                                        ) : (
                                          <></>
                                        )
                                      }
                                      {label.label_name}
                                      {!!label.label_code && ` (${label.label_code})`}
                                    </button>
                                  ))
                                }
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    )
                  }
                </>
              )
            }
            <div
              className="group_title"
              style={{
                userSelect: "none",
                display: "flex",
                justifyContent: "space-between",
                padding: "8px 4px",
              }}
            >
              <div>
                参加しているプロジェクト・業務一覧
              </div>
              {
                login.permissions?.high_volume || (
                  <div>
                    <button
                      type="button"
                      onClick={resetSort}
                      style={{
                        border: "solid 1px #CCCCCC",
                        borderRadius: "4px",
                        fontSize: "10px",
                        padding: "2px 4px",
                      }}
                    >
                      並び替えをリセット
                    </button>
                  </div>
                )
              }
            </div>
            <div className="search_area" style={{ height: "42px" }}>
              <FontAwesomeIcon
                icon={['fas', 'search']}
                className="icon"
              />
              <input
                type="text"
                placeholder={login.permissions?.high_volume ? "プロジェクト・プロジェクトコードを検索" : "プロジェクト・プロジェクトコード・業務・業務コードを検索"}
                onChange={(event) => inputSearchWord(event.target.value)}
                onCompositionStart={() => setComposition(true)}
                onCompositionEnd={() => setComposition(false)}
                value={searchLabelWord}
              />
              {
                searchLabelWord && (
                  <button type="button" className="reset" onClick={resetApp} style={{ right: "8px" }}>
                    <FontAwesomeIcon icon={['far', 'times-circle']} />
                  </button>
                )
              }
            </div>
            {
              isWordLoading ? (
                <LoadingSquare>
                  <>
                    プロジェクトデータを取得しています…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  {
                    !labelGroupList.length ? (
                      <>
                        <div
                          style={{
                            padding: "16px",
                            backgroundColor: "#f5f7ff",
                            textAlign: "center",
                            fontSize: "16px",
                            color: "#999999",
                          }}
                        >
                          {
                            searchLabelWord.length ? (
                              <>
                                {
                                  isSearched ? (
                                    "キーワードに該当するプロジェクトがありませんでした"
                                  ) : (
                                    "検索中…"
                                  )
                                }
                              </>
                            ) : (
                              "キーワードを入力して、プロジェクトを検索してください"
                            )
                          }
                        </div>
                      </>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext items={labelGroupList.map((v) => v.label_group_id.toString())} strategy={rectSortingStrategy}>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                              gap: "8px",
                              padding: "8px",
                              backgroundColor: "#f5f7ff",
                            }}
                          >
                            {
                              labelGroupList?.map((labelGroup) => (
                                <div
                                  key={`label_group_${labelGroup.label_group_id}`}
                                  style={{
                                    position: "relative",
                                    width: `calc((100% - ${(columnNumber - 1) * 8}px) / ${columnNumber})`,
                                  }}
                                >
                                  <div
                                    style={{
                                      position: "relative",
                                    }}
                                  >
                                    <SortableItem id={labelGroup.label_group_id.toString()} animation="none">
                                      <div
                                        style={{
                                          border: "solid 1px #CCCCCC",
                                          backgroundColor: "#FFFFFF",
                                          width: "100%",
                                          cursor: "move",
                                        }}
                                      >
                                        <div
                                          className="group_title"
                                          style={{
                                            display: "flex",
                                            gap: "4px",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            padding: "12px",
                                            minHeight: "42px",
                                            userSelect: "none",
                                            margin: 0,
                                            backgroundColor: openLabelGroupIds.includes(labelGroup.label_group_id) ? "#FAFAFA" : "#FFFFFF",
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: "4px",
                                              alignItems: "flex-start",
                                            }}
                                          >
                                            {
                                              login.permissions?.high_volume || (
                                                <FontAwesomeIcon icon={['fas', 'grip-vertical']} />
                                              )
                                            }
                                            {
                                              labelGroup.label_group_color ? (
                                                <>
                                                  <div
                                                    style={{
                                                      backgroundColor: labelGroup.label_group_color,
                                                      width: "12px",
                                                      height: "12px",
                                                    }}
                                                  />
                                                </>
                                              ) : (
                                                <></>
                                              )
                                            }
                                            <div
                                              style={{
                                                textOverflow: "ellipsis",
                                                overflow: "hidden",
                                                whiteSpace: openLabelGroupIds.includes(labelGroup.label_group_id) ? "normal" : "nowrap",
                                                maxWidth: `${blockWidth - 90}px`,
                                              }}
                                            >
                                              {labelGroup.label_group_name}
                                              {!!labelGroup.label_group_code && ` (${labelGroup.label_group_code})`}
                                            </div>
                                          </div>
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: "4px",
                                              alignItems: "center",
                                            }}
                                          >
                                            {
                                              !searchLabelWord && !openLabelGroupIds.includes(labelGroup.label_group_id) && (
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "baseline",
                                                    fontSize: "8px",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      fontSize: "14px",
                                                    }}
                                                  >
                                                    {labelGroup.labels.length}
                                                  </div>
                                                  件の業務
                                                </div>
                                              )
                                            }
                                            <div
                                              style={{
                                                display: "flex",
                                                gap: "2px",
                                                alignItems: "center",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: "8px",
                                                }}
                                              >
                                                {openLabelGroupIds.includes(labelGroup.label_group_id) ? "閉じる" : "開く"}
                                              </div>
                                              <FontAwesomeIcon
                                                icon={['fas', openLabelGroupIds.includes(labelGroup.label_group_id) ? 'angle-down' : 'angle-right']}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </SortableItem>
                                    <div
                                      style={{
                                        width: login.permissions?.high_volume ? "100%" : "calc(100% - 23px)",
                                        cursor: "pointer",
                                        pointerEvents: "all",
                                        position: "absolute",
                                        top: login.permissions?.high_volume ? "0" : "1px",
                                        left: login.permissions?.high_volume ? "0" : "22px",
                                        height: "100%",
                                        zIndex: 1,
                                      }}
                                      role="none"
                                      onClick={() => changeOpenLabelGroup(labelGroup.label_group_id)}
                                    />
                                  </div>
                                  {
                                    openLabelGroupIds.includes(labelGroup.label_group_id) && (
                                      <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={(e) => handleDragEndForLabel(e, labelGroup.label_group_id)}
                                      >
                                        {/* eslint-disable-next-line max-len */}
                                        <SortableContext items={labelGroup.labels.filter((label) => !label.disabled).map((v) => v.label_id.toString())} strategy={CustomStrategy}>
                                          <div
                                            className="chip_list"
                                            style={{
                                              border: "solid 1px #CCCCCC",
                                              backgroundColor: "#FFFFFF",
                                              marginTop: "-1px",
                                            }}
                                          >
                                            {
                                              labelGroup.labels.filter((label) => !label.disabled).map((label) => (
                                                <div style={{ position: "relative" }}>
                                                  <SortableItem id={label.label_id.toString()} animation="none">
                                                    <button
                                                      type="button"
                                                      className={`chip ${selectLabelIds.includes(label.label_id) ? "checked" : ""}`}
                                                      key={`label_${label.label_id}`}
                                                    >
                                                      {
                                                        login.permissions?.high_volume || (
                                                          <FontAwesomeIcon icon={['fas', 'grip-vertical']} />
                                                        )
                                                      }
                                                      {
                                                        label.label_color ? (
                                                          <>
                                                            <div
                                                              style={{
                                                                backgroundColor: label.label_color,
                                                                borderRadius: "50%",
                                                                width: "12px",
                                                                height: "12px",
                                                              }}
                                                            />
                                                          </>
                                                        ) : (
                                                          <></>
                                                        )
                                                      }
                                                      {label.label_name}
                                                      {!!label.label_code && ` (${label.label_code})`}
                                                    </button>
                                                  </SortableItem>
                                                  <div
                                                    role="none"
                                                    onClick={() => clickLabel(labelGroup, label)}
                                                    onMouseMove={(e) => displayLabelDescription(label.description, e)}
                                                    onMouseLeave={() => hideLabelDescription()}
                                                    onFocus={() => { /* none */ }}
                                                    style={{
                                                      width: login.permissions?.high_volume ? "100%" : "calc(100% - 21px)",
                                                      borderRadius: "0 14px 14px 0",
                                                      position: "absolute",
                                                      top: "1px",
                                                      left: login.permissions?.high_volume ? "0" : "20px",
                                                      height: "28px",
                                                    }}
                                                  />
                                                </div>
                                              ))
                                            }
                                          </div>
                                        </SortableContext>
                                      </DndContext>
                                    )
                                  }
                                </div>
                              ))
                            }
                          </div>
                        </SortableContext>
                      </DndContext>
                    )
                  }
                </>
              )
            }
          </div>
        </div>
      </div>
      <div className="close_screen gray" style={{ zIndex: "1002000" }} role="presentation" onClick={close} />
    </>
  )
}

AddLabelBigModal.defaultProps = {
  isClear: false,
  title: "作業データを業務仕分け",
  date: nowDate("YYYY-MM-DD"),
}

export default AddLabelBigModal
